import { deleteCookie, getCookie, setCookie } from '@luna/cookies';
import { isCsrfFeatureEnabled } from '@shared/environment';
import { logout } from '@shared/auth';
import { createSingleton } from '@luna/utils';
import { request } from '@luna/api';
import { getEnv } from '@luna/env';
import type { RetailEnvironmentConfig } from '@retail/core-config';

const STORAGE_KEY = 'gt';
const TWO_HOURS = 2;

export function clearRetailTokenFromPersistentStorage(): void {
    deleteCookie(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (isCsrfFeatureEnabled()) {
        deleteCookie('gt_csrf');
    }
}

function findToken(): string | undefined {
    let token: string | undefined = getRetailTokenFromStorage();

    if (!token) {
        const urlParams = new URLSearchParams(window.location.hash.substring(1));
        token = urlParams.get('access_token') ?? urlParams.get('/access_token') ?? undefined;
    }

    if (token === 'undefined') {
        token = undefined;
    }

    return token;
}

const { get: getTokenInMemory, set: setTokenInMemory } = createSingleton(findToken());

export function setRetailToken(token: string): void {
    if (getEnv().oidc) {
        sessionStorage.setItem(STORAGE_KEY, token);
    } else {
        setCookie(STORAGE_KEY, token, TWO_HOURS);
    }
    setTokenInMemory(token);
}

export function getRetailTokenFromMemory(): string | null | undefined {
    return getTokenInMemory();
}

export function getRetailTokenFromStorage(): string {
    return sessionStorage.getItem(STORAGE_KEY) ?? getCookie(STORAGE_KEY);
}

export function findRetailTokenOrLogout(): string {
    const tokenInMemory: string | undefined | null = getTokenInMemory();

    if (tokenInMemory) {
        return tokenInMemory;
    }

    const token = findToken();

    if (!token) {
        logout('emptyToken');
        return '';
    }

    setRetailToken(token);

    return token;
}

export function killToken(): Promise<void> {
    return request(getEnv<RetailEnvironmentConfig>().invalidateTokenUrl, {
        method: 'DELETE',
        credentials: 'include',
    })
        .then(clearRetailTokenFromPersistentStorage)
        .catch((e) => {
            clearRetailTokenFromPersistentStorage();
            throw e;
        });
}
